"use client";

/**
 * @file Search Analyticsサイドバーのクライアントサイドソートコンポーネント
 * クライアントサイドのソート機能のみを処理
 */

import { Trans, useLingui } from "@lingui/react/macro";
import { type FragmentOf, readFragment } from "gql.tada";
import {
  ChartLineIcon,
  Loader2Icon,
  MoreHorizontalIcon,
  SearchIcon,
  SettingsIcon,
  XCircleIcon,
} from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { type MouseEvent, useMemo, useState } from "react";
import { toast } from "sonner";
import { useClickAction } from "@/app/[lang]/_hooks/use-click-action";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { SA_POLICIES } from "@/lib/authorization/policies/search-analytics";
import { useCheckPolicy } from "@/lib/authorization/use-check-policy";
import { useRouter } from "@/lib/navigation";
import { cn } from "@/lib/utils";
import { useViewOrder } from "../../../_contexts/view-order-context";
import { useRefreshWorkspaceSidebar } from "../../../_contexts/workspace-sidebar-data-context";
import { cancelSearchAnalyticsOnboarding } from "../../../action";
import {
  deriveSearchAnalyticsSetupStatus,
  shouldShowSearchAnalyticsSetupBadge,
} from "../../../onboarding/search-analytics/_components/setup-status";
import { updateViewOrder } from "../../../sa/[projectId]/(top)/board/action";
import { useUpdateViewOrder } from "../../sortable/sortable-hooks";
import SortableSidebarGroup from "../../sortable/sortable-sidebar-group";
import {
  type SASidebarItem,
  SPECIAL_VIEW_IDS,
} from "../../sortable/sortable-types";
import { isPathActive, reorderViewsByIds } from "../../sortable/sortable-utils";
import { SearchAnalyticsProjectItemFragment } from "./fragments";
import { useSidebarSearchItemVisibility } from "./sidebar-search-context";

interface SortableProjectViewsProps {
  project: FragmentOf<typeof SearchAnalyticsProjectItemFragment>;
  workspaceId: string;
  onboardingSession?: {
    projectID: string;
    status: string;
    latestSnapshotID?: string | null;
  };
}

export function SaSidebarSortable({
  project: projectFragment,
  workspaceId,
  onboardingSession,
}: SortableProjectViewsProps) {
  const project = readFragment(
    SearchAnalyticsProjectItemFragment,
    projectFragment,
  );
  const searchAnalytics = project.searchAnalytics;
  const checkPolicy = useCheckPolicy();
  const canEditViews = checkPolicy(SA_POLICIES["sa-view:edit"]).ok;
  const setupStatus = deriveSearchAnalyticsSetupStatus({
    session: onboardingSession ?? null,
  });

  /** Contextを使用（IDの順序のみを管理） */
  const { getViewOrder } = useViewOrder();
  // 共通フックで楽観的更新とエラーハンドリングを処理
  const handleUpdateViewOrder = useUpdateViewOrder(updateViewOrder);

  // ストアからIDの並び順を取得
  const storedOrderIds = getViewOrder(project.id);

  // ストアに保存されているID順序があればそれを使用してビューを並び替え
  const orderedViews = useMemo(
    () => reorderViewsByIds(searchAnalytics?.views || [], storedOrderIds),
    [storedOrderIds, searchAnalytics?.views],
  );

  // ソート機能用のビュー配列を作成
  const views: SASidebarItem[] = useMemo(() => {
    if (!searchAnalytics) {
      // 後方互換性のため、設定画面だけは表示する（編集権限がある場合のみ）
      return canEditViews
        ? [
            {
              id: SPECIAL_VIEW_IDS.SETTINGS,
              name: "Settings",
              workspaceId,
              projectId: project.id,
              projectType: "sa",
              latestDataDate: "",
              sortable: false,
            },
          ]
        : [];
    }
    const result: SASidebarItem[] = [];
    const latestDataDate = searchAnalytics.bigQuery?.latestDataDate;

    // "All"ビューを追加（ソート不可、常に最初）
    if (latestDataDate) {
      result.push({
        id: SPECIAL_VIEW_IDS.ALL,
        name: "All",
        workspaceId,
        projectId: project.id,
        projectType: "sa",
        latestDataDate,
        isAllCard: true,
        sortable: false,
      });
    }

    result.push(
      ...orderedViews.map((view) => ({
        id: view.id,
        name: view.name,
        workspaceId,
        projectId: project.id,
        projectType: "sa" as const,
        latestDataDate: latestDataDate || "",
        sortable: canEditViews,
      })),
    );

    // "Settings"ビューを追加（ソート不可、常に最後・編集権限がある場合のみ）
    if (canEditViews) {
      result.push({
        id: SPECIAL_VIEW_IDS.SETTINGS,
        name: "Settings",
        workspaceId,
        projectId: project.id,
        projectType: "sa",
        latestDataDate: "",
        sortable: false,
      });
    }

    return result;
  }, [orderedViews, workspaceId, project.id, searchAnalytics, canEditViews]);

  // ビューアイテムのhrefを生成
  const generateViewHref = (item: SASidebarItem): string => {
    if (item.isAllCard) {
      return `/${encodeURIComponent(
        workspaceId,
      )}/sa/${encodeURIComponent(project.id)}/default`;
    }

    if (item.id === SPECIAL_VIEW_IDS.SETTINGS) {
      return `/${encodeURIComponent(
        workspaceId,
      )}/sa/${encodeURIComponent(project.id)}/settings`;
    }

    return `/${encodeURIComponent(
      workspaceId,
    )}/sa/${encodeURIComponent(project.id)}/${encodeURIComponent(item.id)}`;
  };

  // ビューコンテンツをレンダリング
  const renderViewContent = (item: SASidebarItem): React.ReactNode => {
    if (item.isAllCard) {
      return (
        <>
          <ChartLineIcon className="w-4 h-4" />
          <Trans id="default-view.name">All</Trans>
        </>
      );
    }

    if (item.id === SPECIAL_VIEW_IDS.SETTINGS) {
      return (
        <>
          <SettingsIcon className="w-4 h-4" />
          <Trans>Settings</Trans>
        </>
      );
    }

    return (
      <>
        <ChartLineIcon className="w-4 h-4 shrink-0" />
        <div className="line-clamp-2 min-w-0" title={item.name}>
          {item.name}
        </div>
      </>
    );
  };

  return (
    <>
      {onboardingSession ? (
        <ProjectInitialAnalysisItem
          workspaceId={workspaceId}
          projectId={project.id}
          status={setupStatus}
        />
      ) : null}
      <SortableSidebarGroup
        views={views}
        updateViewOrder={handleUpdateViewOrder}
        projectId={project.id}
        projectType="sa"
        actionId={searchAnalytics?.id || "settings-only"}
        className="h-auto min-h-7"
        generateViewHref={generateViewHref}
        renderViewContent={renderViewContent}
        isReorderEnabled={canEditViews}
      />
    </>
  );
}

function ProjectInitialAnalysisItem({
  workspaceId,
  projectId,
  status,
}: {
  workspaceId: string;
  projectId: string;
  status: ReturnType<typeof deriveSearchAnalyticsSetupStatus>;
}) {
  const pathname = usePathname();
  const isVisible = useSidebarSearchItemVisibility(
    "sa",
    projectId,
    SPECIAL_VIEW_IDS.INITIAL_ANALYSIS,
  );
  const href = `/${encodeURIComponent(workspaceId)}/sa/${encodeURIComponent(projectId)}/onboarding`;
  const isActive = isPathActive(pathname, href);

  if (!isVisible) {
    return null;
  }

  return (
    <SidebarMenuSub>
      <SidebarMenuSubItem
        className="w-full"
        style={{ width: "calc(100% + 8px)" }}
      >
        <div className="group/initial-analysis relative">
          <SidebarMenuSubButton
            asChild
            isActive={isActive}
            className={cn(
              "w-full min-w-0",
              isActive && "bg-primary/10! text-primary! [&_svg]:text-primary!",
            )}
          >
            <Link href={href}>
              <SearchIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">
                <Trans>Continue initial analysis</Trans>
              </span>
              <SearchAnalyticsSetupBadge status={status} />
            </Link>
          </SidebarMenuSubButton>
          <ProjectInitialAnalysisActions
            workspaceId={workspaceId}
            projectId={projectId}
            onboardingHref={href}
          />
        </div>
      </SidebarMenuSubItem>
    </SidebarMenuSub>
  );
}

function ProjectInitialAnalysisActions({
  workspaceId,
  projectId,
  onboardingHref,
}: {
  workspaceId: string;
  projectId: string;
  onboardingHref: string;
}) {
  const { t } = useLingui();
  const pathname = usePathname();
  const router = useRouter();
  const refreshWorkspaceSidebar = useRefreshWorkspaceSidebar();
  const [isOpen, setIsOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [state, triggerCancel, isPending] = useClickAction(
    async () => {
      const result = await cancelSearchAnalyticsOnboarding(projectId);
      if (result.errors.length === 0) {
        refreshWorkspaceSidebar("onboarding-mutation");
        toast.success(t`Initial analysis cancelled`, {
          description: t`The project was kept. You can start another initial analysis later.`,
        });
        setIsOpen(false);
        if (isPathActive(pathname, onboardingHref)) {
          router.push(
            `/${encodeURIComponent(workspaceId)}/sa/${encodeURIComponent(projectId)}`,
          );
        }
        router.refresh();
      } else {
        toast.error(t`Failed to cancel initial analysis`, {
          description: result.errors.join(", "),
        });
      }
      return result;
    },
    { errors: [] },
  );

  const handleCancel = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    triggerCancel();
  };

  return (
    <>
      <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <div
          className={cn(
            "pointer-events-none absolute top-0 right-0 z-10 flex h-7 items-center opacity-0 transition-opacity group-hover/initial-analysis:pointer-events-auto group-hover/initial-analysis:opacity-100 group-focus-within/initial-analysis:pointer-events-auto group-focus-within/initial-analysis:opacity-100",
            isMenuOpen && "pointer-events-auto opacity-100",
          )}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t`Initial analysis options`}
              className="h-6 w-6 bg-sidebar shadow-sm"
            >
              <MoreHorizontalIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            side="bottom"
            sideOffset={4}
            alignOffset={-80}
            className="w-auto min-w-[12rem]"
            onCloseAutoFocus={(e) => e.preventDefault()}
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenuItem
              className="text-destructive focus:bg-destructive/10 focus:text-destructive"
              onSelect={() => {
                setIsOpen(true);
              }}
            >
              <XCircleIcon className="mr-2 h-4 w-4" aria-hidden="true" />
              <Trans>Cancel initial analysis</Trans>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </div>
      </DropdownMenu>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          showCloseButton={false}
          onEscapeKeyDown={(event) => {
            if (isPending) {
              event.preventDefault();
            }
          }}
          onInteractOutside={(event) => {
            if (isPending) {
              event.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>
              <Trans>Cancel initial analysis?</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>
                This deletes the unfinished initial analysis data. The project
                itself will not be deleted.
              </Trans>
            </DialogDescription>
          </DialogHeader>
          {state.errors.length > 0 ? (
            <p className="text-destructive text-sm">
              {state.errors.join(", ")}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={isPending}>
                <Trans>Keep it</Trans>
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="destructive"
              onClick={handleCancel}
              disabled={isPending}
            >
              {isPending ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : (
                <Trans>Cancel initial analysis</Trans>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SearchAnalyticsSetupBadge({
  status,
}: {
  status: ReturnType<typeof deriveSearchAnalyticsSetupStatus>;
}) {
  if (!shouldShowSearchAnalyticsSetupBadge(status)) {
    return null;
  }

  return (
    <div className="ml-auto shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
      <Trans>Incomplete</Trans>
    </div>
  );
}
